import React from "react";
import { Link } from "react-router-dom";
import { Briefcase, Home, MapPin, Navigation, Phone, User } from "react-feather";
import StatusBadge from "../../../../components/common/StatusBadge";
import CompactInfoBlock, { CompactInfoField } from "../../../../components/common/CompactInfoBlock";
import { MAROON } from "../../../../components/common/brandColors";
import NewCustomerTag from "./NewCustomerTag";
import { SURVEY_STATUS_CLASS, SURVEY_STATUS_LABEL, formatDateTime, mapsUrl } from "../surveyStatusMaps";
import { paths } from "../../../../routes/paths";

const TrailStep = ({ label, by, date, children }) => {
  if (!by && !date && !children) return null;
  return (
    <span className="me-4 mb-1 d-inline-block" style={{ fontSize: 12.5 }}>
      <span className="text-muted me-1">{label}</span>
      {by && <span className="fw-semibold me-1">{by}</span>}
      {date && <span className="text-muted">{formatDateTime(date)}</span>}
      {children}
    </span>
  );
};

// Gradient header block, same house style as Technical Work's detail.
const SurveyHeader = ({ survey }) => {
  const s = survey;
  const party = s.isProspect ? s.prospectName || s.customerName : s.customerName;
  const contact = [s.contactPerson, s.contactDesignation && `(${s.contactDesignation})`].filter(Boolean).join(" ");
  const hasGps = s.latitude != null && s.longitude != null;

  return (
    <div
      className="p-3 mb-3 rounded"
      style={{ background: `linear-gradient(135deg, ${MAROON}1A 0%, ${MAROON}05 100%)`, border: `1px solid ${MAROON}33` }}
    >
      <div className="d-flex flex-wrap justify-content-between align-items-start">
        <div className="me-3">
          <div className="tx-party" style={{ fontSize: 18 }}>
            {party || "-"}
            {s.isProspect && <NewCustomerTag />}
          </div>
          <div className="text-muted small">
            {s.surveyNumber} · {s.serviceName || "no service chosen"}
          </div>
        </div>
        <div className="text-end">
          <StatusBadge status={s.status} classMap={SURVEY_STATUS_CLASS} labelMap={SURVEY_STATUS_LABEL} />
        </div>
      </div>

      <CompactInfoBlock className="mt-3">
        <CompactInfoField label="Site" value={s.siteLabel} icon={MapPin} />
        <CompactInfoField label="Address" value={s.siteAddress} icon={Home} />
        <CompactInfoField label="Contact" value={contact} icon={User} />
        <CompactInfoField label="Mobile" value={s.contactMobile} icon={Phone} />
        {s.isProspect && <CompactInfoField label="Prospect phone" value={s.prospectPhone} icon={Phone} />}
        {s.isProspect && <CompactInfoField label="Area" value={s.prospectArea} icon={MapPin} />}
        <CompactInfoField label="Branch" value={s.branchCode} icon={Briefcase} />
        {hasGps && (
          <span className="d-inline-flex align-items-center me-4 mb-1" style={{ fontSize: 13 }}>
            <Navigation size={13} className="text-muted me-1" />
            <span className="text-muted me-1">GPS</span>
            <a href={mapsUrl(s.latitude, s.longitude)} target="_blank" rel="noopener noreferrer" className="fw-semibold">
              {Number(s.latitude).toFixed(5)}, {Number(s.longitude).toFixed(5)}
            </a>
            {s.accuracyMeters != null && <span className="text-muted ms-1">±{Math.round(s.accuracyMeters)} m</span>}
          </span>
        )}
      </CompactInfoBlock>

      <div className="mt-2 d-flex flex-wrap">
        <TrailStep label="Created" by={s.createdByName} date={s.createdDate} />
        <TrailStep label="Edited" date={s.modifiedDate} />
        <TrailStep label="Completed" by={s.completedByName} date={s.completedDate} />
        {s.status === "QUOTED" || s.quotationRecordGuid ? (
          <TrailStep label="Quoted" by={s.quotedByName} date={s.quotedDate}>
            {s.quotationRecordGuid && (
              <Link to={paths.quotationDetail(s.quotationRecordGuid)} className="fw-semibold ms-1">
                {s.quotationNumber || "open quotation"}
              </Link>
            )}
          </TrailStep>
        ) : null}
        <TrailStep label="Cancelled" date={s.cancelledDate} />
      </div>
      {s.status === "CANCELLED" && s.cancelledReason && (
        <div className="small mt-1 text-danger">Cancelled: {s.cancelledReason}</div>
      )}
    </div>
  );
};

export default SurveyHeader;
